import {
	Award,
	ChevronLeft,
	ChevronRight,
	ExternalLink,
	EyeOff,
	GitMerge,
	Search,
	Store,
	X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson } from "../api/rest";
import { PageHeader } from "../components/layout/PageHeader";
import {
	type BrandListItem,
	type BrandSearchConsole,
	type BrandSort,
	type BrandStats,
	type BrandsResponse,
	brandPageUrl,
	INDEXABLE_COMPARABLE,
} from "../types/brands";

// ---------------------------------------------------------------------------
// Brands list
//
// The window onto the `run_brands` rollup. Until this page existed there was no way to answer "are
// these brands real?" — the only brand feature in the admin read `product_attributes` directly for
// a filter dropdown and never touched the `brands` table.
// ---------------------------------------------------------------------------

type Filter =
	| "all"
	| "indexable"
	| "thin"
	| "suspect"
	| "collisions"
	| "hidden"
	| "merged";

const FILTERS: { id: Filter; label: string; hint: string }[] = [
	{ id: "all", label: "All", hint: "Every brand with at least one product" },
	{
		id: "indexable",
		label: "Indexable",
		hint: `≥ ${INDEXABLE_COMPARABLE} comparable products, not hidden, not merged — the set in the sitemap`,
	},
	{
		id: "thin",
		label: "Thin",
		hint: "Renders noindex — too few comparable products, hidden, or merged",
	},
	{
		id: "suspect",
		label: "Shop names",
		hint: "Brand name matches a validated merchant's name",
	},
	{
		id: "collisions",
		label: "Alias collisions",
		hint: "More than one feed spelling behind this slug",
	},
	{
		id: "hidden",
		label: "Hidden",
		hint: "Curated out of the hub and the sitemap",
	},
	{
		id: "merged",
		label: "Merged",
		hint: "Folded into another brand; the URL 301s",
	},
];

const filterParams = (filter: Filter): Record<string, string> => {
	switch (filter) {
		case "indexable":
			return { indexable: "1" };
		case "thin":
			return { indexable: "0" };
		case "suspect":
			return { suspect: "1" };
		case "collisions":
			return { collisions: "1" };
		case "hidden":
			return { hidden: "1" };
		case "merged":
			return { merged: "1" };
		default:
			return {};
	}
};

const SORTS: { id: BrandSort; label: string }[] = [
	{ id: "comparable", label: "Most comparable" },
	{ id: "products", label: "Most products" },
	{ id: "name", label: "Name A–Z" },
	{ id: "aliases", label: "Most aliases" },
];

const StatCard = ({
	label,
	value,
	tone = "slate",
}: {
	label: string;
	value: string | number;
	tone?: "slate" | "amber" | "emerald";
}) => (
	<div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
		<p className="font-medium text-[0.7rem] text-zinc-500 uppercase tracking-[0.14em]">
			{label}
		</p>
		<p className="tnum mt-2 flex items-center gap-2 font-semibold text-[22px] text-ink">
			{typeof value === "number" ? value.toLocaleString() : value}
			{tone !== "slate" && (
				<span
					className={`size-1.5 rounded-full ${tone === "amber" ? "bg-amber-500" : "bg-emerald-500"}`}
				/>
			)}
		</p>
	</div>
);

/**
 * The rollup only runs in `run_sync` phase 7, so a stale table is the likeliest reason a curation
 * change has not reached the storefront. Saying how old it is beats saying when it was.
 */
const rebuildAge = (lastRebuild: string | null) => {
	if (!lastRebuild) return { text: "never", stale: true };
	const then = new Date(lastRebuild);
	const hours = (Date.now() - then.getTime()) / 36e5;
	if (!Number.isFinite(hours)) return { text: "never", stale: true };
	const text =
		hours < 1
			? "just now"
			: hours < 48
				? `${Math.round(hours)}h ago`
				: `${Math.round(hours / 24)}d ago`;
	return { text, stale: hours > 36 };
};

const Brands = () => {
	const [brands, setBrands] = useState<BrandListItem[]>([]);
	const [stats, setStats] = useState<BrandStats | null>(null);
	const [searchConsole, setSearchConsole] = useState<BrandSearchConsole | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [filter, setFilter] = useState<Filter>("all");
	const [sort, setSort] = useState<BrandSort>("comparable");

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const params = new URLSearchParams({
				page: String(page),
				sort,
				...filterParams(filter),
			});
			if (search) params.set("q", search);

			const [list, statRows, sc] = await Promise.all([
				fetchJson<BrandsResponse>(`/brands?${params}`),
				fetchJson<BrandStats>("/brands/stats"),
				fetchJson<BrandSearchConsole>("/brands/search-console"),
			]);
			setBrands(list.items);
			setTotalPages(list.totalPages);
			setTotal(list.total);
			setStats(statRows);
			setSearchConsole(sc);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setLoading(false);
		}
	}, [page, search, filter, sort]);

	useEffect(() => {
		load();
	}, [load]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(1);
		setSearch(searchInput);
	};

	const rebuild = rebuildAge(stats?.lastRebuild ?? null);

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Shop · Brands"
				title="Brands."
				qualifier="The pages behind /maerke."
				description={
					<>
						<p>
							The <code className="text-xs">brands</code> rollup behind{" "}
							<span className="font-mono text-xs">/maerke/&lt;slug&gt;</span> —
							rebuilt nightly by <code className="text-xs">run_brands</code> in
							sync phase 7.
						</p>
						<p
							className={`mt-2 flex items-center gap-2 text-xs ${rebuild.stale ? "font-medium text-zinc-700" : "text-zinc-400"}`}
							title={
								stats?.lastRebuild
									? new Date(stats.lastRebuild).toLocaleString()
									: undefined
							}
						>
							<span
								className={`size-1.5 rounded-full ${rebuild.stale ? "bg-amber-500" : "bg-emerald-500"}`}
							/>
							Last rebuild: {rebuild.text}
						</p>
					</>
				}
			/>

			{stats && (
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
					<StatCard label="Brands" value={stats.total} />
					<StatCard label="Indexable" value={stats.indexable} tone="emerald" />
					<StatCard
						label="Shop names"
						value={stats.suspect}
						tone={stats.suspect > 0 ? "amber" : "slate"}
					/>
					<StatCard
						label="Alias collisions"
						value={stats.aliasCollisions}
						tone={stats.aliasCollisions > 0 ? "amber" : "slate"}
					/>
					<StatCard label="Hidden" value={stats.hidden} />
					<StatCard label="Merged" value={stats.merged} />
				</div>
			)}

			{searchConsole && (
				// The /maerke/ page group in Search Console. Reads zero until Google has crawled the
				// pages — which, for the first weeks after launch, is the number to watch.
				<div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
					<span className="font-semibold text-slate-700">
						Search Console · /maerke/
					</span>
					<span className="text-slate-400">
						{" "}
						· last {searchConsole.days} days
					</span>
					<span className="ml-3 text-slate-600">
						{searchConsole.impressions.toLocaleString()} impressions ·{" "}
						{searchConsole.clicks.toLocaleString()} clicks ·{" "}
						{searchConsole.urlsWithImpressions.toLocaleString()} URLs served
						{searchConsole.avgPosition !== null &&
							` · avg. position ${searchConsole.avgPosition.toFixed(1)}`}
					</span>
				</div>
			)}

			<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
				<form onSubmit={handleSearch} className="relative flex-1 max-w-xl">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
					<input
						type="text"
						placeholder="Search by name, slug or feed spelling…"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/10 transition-all"
					/>
					{search && (
						<button
							type="button"
							onClick={() => {
								setSearch("");
								setSearchInput("");
								setPage(1);
							}}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
						>
							<X className="w-4 h-4" />
						</button>
					)}
				</form>

				<select
					value={sort}
					onChange={(e) => {
						setSort(e.target.value as BrandSort);
						setPage(1);
					}}
					className="px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-200 bg-white text-slate-600 cursor-pointer outline-none focus:border-primary/40"
				>
					{SORTS.map((s) => (
						<option key={s.id} value={s.id}>
							{s.label}
						</option>
					))}
				</select>
			</div>

			<div className="flex items-center gap-2 flex-wrap">
				{FILTERS.map((f) => (
					<button
						key={f.id}
						type="button"
						title={f.hint}
						onClick={() => {
							setFilter(f.id);
							setPage(1);
						}}
						className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
							filter === f.id
								? "bg-primary text-white border-primary"
								: "border-slate-200 bg-white text-slate-600 hover:border-primary/40 hover:text-primary"
						}`}
					>
						{f.label}
					</button>
				))}
			</div>

			{error ? (
				<div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
					{error}
				</div>
			) : loading ? (
				<div className="flex items-center justify-center py-20">
					<div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
				</div>
			) : brands.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-20 text-slate-400">
					<Award className="w-16 h-16 mb-4 text-slate-300" />
					<p className="text-lg font-semibold text-slate-500">
						No brands found
					</p>
					<p className="text-sm">
						{total === 0 && filter === "all" && !search
							? "The rollup has not written any rows yet — check that run_brands ran in the last sync."
							: "Try adjusting your search or filters"}
					</p>
				</div>
			) : (
				<div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
									<th className="px-6 py-3">Brand</th>
									<th className="px-6 py-3">Products</th>
									<th className="px-6 py-3">Comparable</th>
									<th className="px-6 py-3">Status</th>
									<th className="px-6 py-3">Root category</th>
									<th className="px-6 py-3">Aliases</th>
									<th className="px-6 py-3">Page</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{brands.map((b) => (
									<tr
										key={b.slug}
										className="hover:bg-slate-50/50 transition-colors"
									>
										<td className="px-6 py-3.5">
											<Link
												to={`/brands/${encodeURIComponent(b.slug)}`}
												className="group block"
											>
												<span className="font-medium text-slate-700 group-hover:text-primary transition-colors">
													{b.name}
												</span>
												<span className="block font-mono text-[11px] text-slate-400">
													{b.slug}
												</span>
											</Link>
										</td>
										<td className="px-6 py-3.5 text-slate-600">
											{b.productCount.toLocaleString()}
										</td>
										<td className="px-6 py-3.5 text-slate-600">
											{b.comparableCount.toLocaleString()}
										</td>
										<td className="px-6 py-3.5">
											<div className="flex flex-wrap items-center gap-1.5">
												{b.indexable ? (
													<span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">
														Indexable
													</span>
												) : (
													<span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500">
														noindex
													</span>
												)}
												{b.hidden && (
													<span
														title="Hidden by an override"
														className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-200 text-slate-600"
													>
														<EyeOff className="w-3 h-3" /> Hidden
													</span>
												)}
												{b.mergedInto && (
													<span
														title={`Merged into ${b.mergedInto}`}
														className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-100 text-violet-700"
													>
														<GitMerge className="w-3 h-3" /> {b.mergedInto}
													</span>
												)}
												{b.suspectMerchantName && (
													<span
														title="This brand name matches a validated merchant's name — it may be a shop, not a brand"
														className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700"
													>
														<Store className="w-3 h-3" /> Shop name
													</span>
												)}
											</div>
										</td>
										<td className="px-6 py-3.5 text-xs text-slate-500">
											{b.rootCategoryName ?? (
												<span className="text-slate-300">–</span>
											)}
										</td>
										<td className="px-6 py-3.5">
											{b.aliasCount > 1 ? (
												<span className="text-xs font-medium text-amber-600">
													{b.aliasCount} spellings
												</span>
											) : (
												<span className="text-xs text-slate-400">1</span>
											)}
										</td>
										<td className="px-6 py-3.5">
											<a
												href={brandPageUrl(b.slug)}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-primary transition-colors"
											>
												<ExternalLink className="w-3 h-3" /> Open
											</a>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{totalPages > 1 && (
						<div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
							<p className="text-xs text-slate-400">
								Page {page} of {totalPages} · {total.toLocaleString()} brands
							</p>
							<div className="flex items-center gap-1">
								<button
									type="button"
									onClick={() => setPage((p) => Math.max(1, p - 1))}
									disabled={page === 1}
									className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
								>
									<ChevronLeft className="w-4 h-4" />
								</button>
								<button
									type="button"
									onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
									disabled={page === totalPages}
									className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
								>
									<ChevronRight className="w-4 h-4" />
								</button>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default Brands;
